import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Star,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Heart,
  Trophy,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';

const ShareStoryPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchingCampaigns, setFetchingCampaigns] = useState(true);
  const [eligibleCampaigns, setEligibleCampaigns] = useState([]);
  const [myStories, setMyStories] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    campaign_id: '',
    title: '',
    story: '',
    amount_raised: '',
    outcome: '',
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=share-story');
      return;
    }
    
    fetchData();
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    setFetchingCampaigns(true);
    try {
      const [campaignsRes, storiesRes] = await Promise.all([
        apiClient.get('/success-stories/my-eligible-campaigns'),
        apiClient.get('/success-stories/my-stories'),
      ]);
      setEligibleCampaigns(campaignsRes.data.campaigns || []);
      setMyStories(storiesRes.data.stories || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setFetchingCampaigns(false);
    }
  };

  const [deletingStory, setDeletingStory] = useState(null);

  const handleDeleteStory = async (storyId) => {
    if (!window.confirm('Are you sure you want to delete this story? This action cannot be undone.')) {
      return;
    }
    
    setDeletingStory(storyId);
    try {
      await apiClient.delete(`/success-stories/${storyId}`);
      // Refresh the stories list
      await fetchData();
      setSuccess('Story deleted successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete story');
    } finally {
      setDeletingStory(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Frontend validation - only allow positive whole numbers (no 0, no decimals, no negatives)
    const amountStr = formData.amount_raised.trim();
    const amount = parseInt(amountStr, 10);
    
    // Check if it's a valid positive integer
    if (!amountStr || isNaN(amount) || amount <= 0 || amountStr.includes('.') || amountStr.includes(',') || amountStr !== String(amount)) {
      setError('Amount Raised must be a positive whole number (no decimals, no zero)');
      setLoading(false);
      return;
    }
    
    if (!formData.campaign_id) {
      setError('Please select a campaign');
      setLoading(false);
      return;
    }
    
    if (!formData.title || formData.title.length < 3) {
      setError('Title must be at least 3 characters');
      setLoading(false);
      return;
    }
    
    if (!formData.story || formData.story.length < 20) {
      setError('Story must be at least 20 characters');
      setLoading(false);
      return;
    }
    
    if (!formData.outcome || formData.outcome.length < 10) {
      setError('Outcome must be at least 10 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.post('/success-stories/submit', {
        ...formData,
        amount_raised: amount,
      });
      setSuccess(response.data.message || 'Story submitted successfully!');
      setFormData({
        campaign_id: '',
        title: '',
        story: '',
        amount_raised: '',
        outcome: '',
      });
      fetchData();
    } catch (err) {
      // Handle Pydantic validation errors (array format)
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
        setError(messages);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to submit story. Please check your inputs.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">Rejected</span>;
      default:
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Pending Review</span>;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center px-4 py-2 bg-amber-500/20 rounded-full mb-4">
              <Star className="w-5 h-5 text-amber-400 mr-2" />
              <span className="text-amber-400 font-medium">Share Your Success</span>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Share Your Success Story</h1>
            <p className="text-muted-foreground">
              Inspire others by sharing how FundTheHope helped you achieve your goals
            </p>
          </div>

          {success && (
            <Alert className="mb-6 bg-emerald-500/10 border-emerald-500/30">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <AlertDescription className="text-emerald-400">{success}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert className="mb-6 bg-red-500/10 border-red-500/30">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <AlertDescription className="text-red-400">{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Submit Form */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  Submit Your Story
                </CardTitle>
                <CardDescription>
                  Tell us about your campaign journey and how it made a difference
                </CardDescription>
              </CardHeader>
              <CardContent>
                {fetchingCampaigns ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : eligibleCampaigns.length === 0 ? (
                  <div className="text-center py-12">
                    <Trophy className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">No Eligible Campaigns Yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Start a campaign and raise funds to share your success story!
                    </p>
                    <Button onClick={() => navigate('/create')} className="bg-gradient-to-r from-emerald-500 to-teal-600">
                      Create Campaign
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label>Select Campaign</Label>
                      <Select
                        value={formData.campaign_id}
                        onValueChange={(value) => setFormData({ ...formData, campaign_id: value })}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Choose a campaign" />
                        </SelectTrigger>
                        <SelectContent>
                          {eligibleCampaigns.map((campaign) => (
                            <SelectItem key={campaign.id} value={campaign.id}>
                              {campaign.title} (${campaign.current_amount?.toLocaleString() || 0} raised)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Story Title</Label>
                      <Input
                        placeholder="e.g., 'How FundTheHope Saved My Family'"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label>Amount Raised ($)</Label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        placeholder="e.g., 5000"
                        value={formData.amount_raised}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow positive whole numbers - no decimals, no negative
                          if (value === '' || /^[1-9]\d*$/.test(value)) {
                            setFormData({ ...formData, amount_raised: value });
                          }
                        }}
                        onKeyDown={(e) => {
                          // Prevent decimal point, minus sign, and 'e'
                          if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === '+') {
                            e.preventDefault();
                          }
                        }}
                        className="mt-2"
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">Must be a positive whole number (no decimals)</p>
                    </div>

                    <div>
                      <Label>Your Story</Label>
                      <Textarea
                        placeholder="Share your experience... What challenge did you face? How did the community help? What was the journey like?"
                        value={formData.story}
                        onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                        className="mt-2 min-h-[150px]"
                        required
                      />
                    </div>

                    <div>
                      <Label>What did you achieve with the funds?</Label>
                      <Textarea
                        placeholder="e.g., 'We were able to pay for the surgery, and now my daughter is healthy and back to school!'"
                        value={formData.outcome}
                        onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                        className="mt-2 min-h-[100px]"
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || !formData.campaign_id}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                      ) : (
                        <><Heart className="w-4 h-4 mr-2" /> Submit My Story</>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* My Stories */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">My Submitted Stories</CardTitle>
              </CardHeader>
              <CardContent>
                {myStories.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No stories submitted yet
                  </p>
                ) : (
                  <div className="space-y-4">
                    {myStories.map((story) => (
                      <div key={story.id} className="p-3 bg-secondary rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-foreground text-sm line-clamp-1">{story.title}</h4>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(story.status)}
                            {(story.status === 'pending' || story.status === 'rejected') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                                onClick={() => handleDeleteStory(story.id)}
                                disabled={deletingStory === story.id}
                              >
                                {deletingStory === story.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3 h-3" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">{story.story}</p>
                        {story.status === 'rejected' && story.rejection_reason && (
                          <p className="text-xs text-red-400 mt-2">
                            Reason: {story.rejection_reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ShareStoryPage;
